import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, Icon, Button, Header, Dropdown, ButtonGroup, Popup } from 'semantic-ui-react';
import { format } from 'date-fns';
import { formatNumber, formatDollars, offtakeBidDecimals, offtakeRateUnit } from '~/utils';
import { usePortfolioContext } from '~/hooks';
import { PricingType, FeeUnitsDisplayShort, FeeUnits, StorageFeeUnits, StorageFeeUnitsDisplayShort } from '~/constants';
import { indexRfisByPortfolioId } from '~/api/requestForInformation';
import * as portfolioApi from '~/api/portfolios';
import { usePermissions } from '~/requests/permissions/usePermissions';
import BudgetTable, { NO_FEE_BASIS_MESSAGE } from '~/features/projectReview/components/BudgetTable';
import RfiResponseList from '~/features/requestForInformation/developer/RfiResponseList';
import ActionsButton from './Actions/ActionsButton';
import ActionsDropdownMenu from './Actions/ActionsDropdownMenu';
import AcceptPortfolioBidModal from './modals/AcceptPortfolioBids';
import DeclinePortfolioBidModal from './modals/DeclinePortfolioBids';
import { InvestorActivityChecklistSingle } from './InvestorActivityChecklist';
import '../styles.scss';

interface PortfolioBidCardsProps {
    sortedBidData: any[];
    budgetDisplayMode: 'unit' | 'total';
}

const PortfolioBidCards: React.FC<PortfolioBidCardsProps> = ({ sortedBidData, budgetDisplayMode }) => {
    const { portfolio, portfolioDisplayValues } = usePortfolioContext();
    const { permissions } = usePermissions();
    const [rfiData, setRfiData] = useState<any[]>([]);
    const [hasSummaryDoc, setHasSummaryDoc] = useState<boolean | undefined>(undefined);

    // Carousel state and refs
    const carouselRef = useRef<HTMLDivElement>(null);
    const carouselRefHeader = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    // Modal state
    const [acceptInvestorId, setAcceptInvestorId] = useState('');
    const [declineInvestorId, setDeclineInvestorId] = useState('');

    useEffect(() => {
        const fetchRfis = async () => {
            try {
                const rfis = await indexRfisByPortfolioId(portfolio.id);
                setRfiData(rfis);
            } catch (error) {
                console.error('Error fetching RFIs for portfolio:', error);
            }
        };

        fetchRfis();
    }, [portfolio.id]);

    useEffect(() => {
        if (!portfolio?.id) return;
        const portfolioId = portfolio.parentId ?? portfolio.id;
        portfolioApi.getPortfolioSummaryDownloadUrl(portfolioId)
            .then((data: { presignedUrl?: string }) => setHasSummaryDoc(!!data?.presignedUrl))
            .catch(() => setHasSummaryDoc(false));
    }, [portfolio?.id, portfolio?.parentId]);

    // Check scroll position to show/hide navigation buttons
    const checkScrollPosition = () => {
        if (carouselRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
            setCanScrollLeft(scrollLeft > 0);
            setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
        }
    };

    // Scroll the carousel
    const scrollCarousel = (direction: 'left' | 'right') => {
        if (carouselRef.current) {
            const cardWidth = 354; // Match max card width
            const scrollAmount = cardWidth + 8; // Card width + gap
            const currentScroll = carouselRef.current.scrollLeft;
            const targetScroll = direction === 'left'
                ? currentScroll - scrollAmount
                : currentScroll + scrollAmount;

            carouselRef.current.scrollTo({
                left: targetScroll,
                behavior: 'smooth'
            });
            carouselRefHeader.current?.scrollTo({
                left: targetScroll,
                behavior: 'smooth'
            });
        }

    };

    // Update scroll position when component mounts or data changes
    useEffect(() => {
        checkScrollPosition();
        const handleResize = () => checkScrollPosition();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [sortedBidData]);

    // Add scroll event listener to carousel
    useEffect(() => {
        const carousel = carouselRef.current;
        if (carousel) {
            carousel.addEventListener('scroll', checkScrollPosition);
            return () => carousel.removeEventListener('scroll', checkScrollPosition);
        }
    }, []);

    // Prevent manual horizontal scrolling; force navigation via controls
    useEffect(() => {
        const preventHorizontalScroll = (event: WheelEvent) => {
            if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
                event.preventDefault();
            }
        };

        const carousel = carouselRef.current;
        const header = carouselRefHeader.current;
        const listenerOptions: any = { passive: false };

        carousel?.addEventListener('wheel', preventHorizontalScroll, listenerOptions);
        header?.addEventListener('wheel', preventHorizontalScroll, listenerOptions);

        return () => {
            carousel?.removeEventListener('wheel', preventHorizontalScroll);
            header?.removeEventListener('wheel', preventHorizontalScroll);
        };
    }, []);

    // Activity checklist by company ID (from dealSummary.investors)
    const activityByCompanyId = React.useMemo(() => {
        const investors = portfolioDisplayValues?.dealSummary?.investors ?? [];
        const map: Record<number, any> = {};
        investors.forEach((inv: any) => {
            if (inv.companyId != null) map[inv.companyId] = inv.activity ?? null;
        });
        return map;
    }, [portfolioDisplayValues?.dealSummary?.investors]);

    // Process RFI data by investor
    const getRfiDataForInvestor = (investorId: string) => {
        if (!rfiData || rfiData.length === 0) return [];

        return rfiData.map(rfi => {
            const investorResponse = rfi.responses?.find((response: any) => response.companyId === investorId);
            return {
                name: rfi.name,
                description: rfi.description,
                content: investorResponse?.content || null,
                snippet: investorResponse?.snippet || '',
                updatedAt: investorResponse?.updatedAt || null
            };
        });
    };

    // Helper function to get system PV size for calculations
    const getSystemPVSizeKwdc = (project: any) => {
        return parseFloat(((project?.projectArrays?.reduce((sum: number, array: any) => sum + (array.sizeKwdc || 0), 0) || 0)).toFixed(3));
    };

    // Helper function to calculate bid values for display
    const calculateBidValue = (investorBidData: any, unit: 'WDC' | 'KWH' | 'TOTAL') => {
        const projectsArray = investorBidData.projects ? Object.values(investorBidData.projects) : [];
        let bidTotal = 0;
        let totalSizeKwdc = 0;
        let count = 0;
        let totalWeightedUnit = 0;
        let totalWeight = 0;

        projectsArray.forEach((project: any) => {
            const bid = project.projectBids?.find((bid: any) => bid.bidStatus === 'PENDING');
            if (bid && bid.amount) {
                const projectSizeKwdc = getSystemPVSizeKwdc(project);
                totalSizeKwdc += projectSizeKwdc;
                count += 1;
                if (bid.unit === 'WDC') {
                    bidTotal += bid.amount * projectSizeKwdc * 1000;
                    totalWeightedUnit += bid.amount * projectSizeKwdc;
                    totalWeight += projectSizeKwdc;
                } else if (bid.unit === 'TOTAL') {
                    bidTotal += bid.amount;
                } else {
                    totalWeightedUnit += bid.amount * projectSizeKwdc;
                    totalWeight += projectSizeKwdc;
                }
            }
        });

        const isProjectDeclined = (project: any) =>
            project?.status === 'DECLINED_TO_BID' || project?.status === 'BID_REJECTED';
        const allDeclined = projectsArray.length > 0 && projectsArray.every(isProjectDeclined);

        if (count === 0) {
            if (allDeclined) {
                return <span className="amount"><i>Declined</i></span>;
            }
            return <span className="amount"><i>Awaiting bids...</i></span>;
        }

        if (count > 0 && count < projectsArray.length) return <span className="amount"><i>Partial...</i></span>;

        if (unit === 'TOTAL') {
            return (
                <>
                    <span className="amount">{formatDollars(bidTotal)}</span>
                </>
            );
        } else if (unit === 'WDC') {
            return (
                <>
                    <span className="amount">{'$' + formatNumber(bidTotal / totalSizeKwdc / 1000, 3)}/Wdc</span>
                </>
            );
        } else {
            if (totalWeight === 0) return <span className="amount"><i>Awaiting bids...</i></span>;
            const contractType = portfolio.projects[0]?.contractType;
            return (
                <>
                    <span className="amount">{'$' + formatNumber(totalWeightedUnit / totalWeight, offtakeBidDecimals(contractType))}/{offtakeRateUnit(contractType)}</span>
                </>
            );
        }
    };

    const getLastUpdated = (investorBidData: any) => {
        const projectsArray = investorBidData.projects ? Object.values(investorBidData.projects) : [];
        let lastUpdatedDate: Date | null = null;
        const consider = (raw: any) => {
            if (!raw) return;
            const d = new Date(raw);
            if (!isNaN(d.getTime()) && (!lastUpdatedDate || d > lastUpdatedDate)) {
                lastUpdatedDate = d;
            }
        };

        projectsArray.forEach((project: any) => {
            const pendingBid = project.projectBids?.find((bid: any) => bid.bidStatus === 'PENDING');
            if (pendingBid) {
                consider(pendingBid.createdAt);
            } else if (project?.status === 'DECLINED_TO_BID' || project?.status === 'BID_REJECTED') {
                consider(project?.updatedAt);
            }
        });

        if (lastUpdatedDate) {
            return format(lastUpdatedDate, 'MMM d, yyyy');
        }
        return '—';
    };

    const renderProjectBudgetTables = (investorBidData: any) => {
        const projectsArray = investorBidData.projects ? Object.values(investorBidData.projects) : [];

        // Sort projects by name in ascending order
        const sortedProjects = projectsArray.sort((a: any, b: any) => {
            const nameA = (a.name || '').toLowerCase();
            const nameB = (b.name || '').toLowerCase();
            return nameA.localeCompare(nameB);
        });


        return sortedProjects.map((project: any, index: number) => {
            const bid = project.projectBids?.find((bid: any) => bid.bidStatus === 'PENDING');
            return (
                <div key={project.id || index} className="project-budget-section">
                    <BudgetTable
                        project={project}
                        formState={bid}
                        viewMode="card"
                        budgetDisplayMode={budgetDisplayMode}
                        uiContext="portfolio"
                    />
                </div>
            );
        });
    };

    // Helper function to calculate total bid value for sorting
    const getTotalBidValue = (investorBidData: any) => {
        const projectsArray = investorBidData.projects ? Object.values(investorBidData.projects) : [];
        let bidTotal = 0;

        projectsArray.forEach((project: any) => {
            const bid = project.projectBids[0];
            if (bid && bid.amount) {
                const projectSizeKwdc = getSystemPVSizeKwdc(project);
                if (bid.unit === 'WDC') {
                    bidTotal += bid.amount * projectSizeKwdc * 1000;
                } else if (bid.unit === 'TOTAL') {
                    bidTotal += bid.amount;
                } else {
                    // For KWH pricing, we need to estimate total value
                    // This is a simplified calculation for sorting purposes
                    bidTotal += bid.amount * projectSizeKwdc * 1000;
                }
            }
        });

        return bidTotal;
    };

    // Sort bid data by total bid value (highest to lowest), then by bid completeness, then by name
    const isInvestorDeclined = (investor: any) => {
        const projects = investor?.projects ? Object.values(investor.projects) : [];
        if (!projects.length) return false;
        return projects.every((p: any) => p?.status === 'DECLINED_TO_BID' || p?.status === 'BID_REJECTED');
    };
    const defaultSortedBidData = [...sortedBidData].sort((a, b) => {
        const aDeclined = isInvestorDeclined(a);
        const bDeclined = isInvestorDeclined(b);
        if (aDeclined !== bDeclined) return aDeclined ? 1 : -1;

        const projectsArrayA = a.projects ? Object.values(a.projects) : [];
        const projectsArrayB = b.projects ? Object.values(b.projects) : [];

        const bidCountA = projectsArrayA.reduce((count: number, project: any) => {
            const bid = project.projectBids?.find((bid: any) => bid.bidStatus === 'PENDING');
            return bid?.amount ? count + 1 : count;
        }, 0);
        const bidCountB = projectsArrayB.reduce((count: number, project: any) => {
            const bid = project.projectBids?.find((bid: any) => bid.bidStatus === 'PENDING');
            return bid?.amount ? count + 1 : count;
        }, 0);

        const totalProjectsA = projectsArrayA.length;
        const totalProjectsB = projectsArrayB.length;

        // Determine bid status
        const getBidStatus = (bidCount: number, totalProjects: number) => {
            if (bidCount === 0) return 3; // No bids
            if (bidCount < totalProjects) return 2; // Partial bids
            return 1; // All bids
        };

        const statusA = getBidStatus(bidCountA, totalProjectsA);
        const statusB = getBidStatus(bidCountB, totalProjectsB);

        // First, separate complete bids from incomplete/no bids
        if (statusA === 1 && statusB !== 1) return -1; // A has complete bids, B doesn't
        if (statusB === 1 && statusA !== 1) return 1;  // B has complete bids, A doesn't

        // If both have complete bids, sort by total bid value (highest first)
        if (statusA === 1 && statusB === 1) {
            const totalValueA = getTotalBidValue(a);
            const totalValueB = getTotalBidValue(b);
            if (totalValueA !== totalValueB) {
                return totalValueB - totalValueA; // Highest value first
            }
        }

        // If both have same status (partial or no bids), sort by bid status then name
        if (statusA !== statusB) {
            return statusA - statusB;
        }

        // If same status, sort by investor name
        const nameA = (a.investor?.company?.name || 'Unknown Investor').toLowerCase();
        const nameB = (b.investor?.company?.name || 'Unknown Investor').toLowerCase();
        return nameA.localeCompare(nameB);
    });

    const getInvestorName = (investorBidData: any) => {
        const fullName = investorBidData.investor?.company?.name || 'Unknown Investor';
        return fullName.length > 28 ? fullName.substring(0, 28) + '...' : fullName;
    };

    const getFeeBasis = () => {
        const investorQueueRow = portfolio.projects[0].investorQueue[0];
        return {
            buyer: {
                fee: investorQueueRow.nrsFee,
                feeUnit: investorQueueRow.nrsFeeUnit,
                storageFee: investorQueueRow.buyerStorageFee,
                storageFeeUnit: investorQueueRow.buyerStorageFeeUnit,
            },
            seller: {
                fee: investorQueueRow.sellerFee,
                feeUnit: investorQueueRow.sellerFeeUnit,
                storageFee: investorQueueRow.sellerStorageFee,
                storageFeeUnit: investorQueueRow.sellerStorageFeeUnit,
            }
        };
    };

    // Whether any project in the portfolio has PV wattage to base a PV fee on.
    const portfolioHasPvWattage = () => {
        return (portfolio.projects || []).some((project: any) => getSystemPVSizeKwdc(project) > 0);
    };

    const buildFeeBasisDiplay = (type: 'buyer' | 'seller') => {
        const feeBasis = getFeeBasis();
        const hasPvWattage = portfolioHasPvWattage();

        if (type === 'buyer') {
            const buyerFee = feeBasis.buyer.fee ? `${formatDollars(feeBasis.buyer.fee, { dec: 3 })}${FeeUnitsDisplayShort[feeBasis.buyer.feeUnit as keyof typeof FeeUnits]}` : '$0';
            const buyerStorageFee = feeBasis.buyer.storageFee ? `${formatDollars(feeBasis.buyer.storageFee, { dec: 3 })}${StorageFeeUnitsDisplayShort[feeBasis.buyer.storageFeeUnit as keyof typeof StorageFeeUnits]}` : null;

            return (
                <>
                    {(permissions.isAdmin || permissions.isInvestor) ? (
                        <Card.Content className="fees-note-content">
                            <div className="fee-basis-display">
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <span>Buyer fees
                                        <Popup
                                            trigger={<Icon name="info circle" style={{ marginLeft: '4px', cursor: 'pointer', color: 'var(--color-secondary)' }} />}
                                            content={buyerFee === '$0' ? (hasPvWattage ? 'Seller pays fees on this deal.' : NO_FEE_BASIS_MESSAGE) : 'Success fee charged to the buyer'}
                                            position="top center"
                                            inverted
                                        />
                                    </span><div>{buyerFee}</div>
                                </div>
                                {buyerStorageFee && (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <span>Buyer storage fees
                                            <Popup
                                                trigger={<Icon name="info circle" style={{ marginLeft: '4px', cursor: 'pointer', color: 'var(--color-secondary)' }} />}
                                                content={'Success fee charged to the buyer'}
                                                position="top center"
                                                inverted
                                            />
                                        </span><div>{buyerStorageFee}</div>
                                    </div>
                                )}
                            </div>
                        </Card.Content>
                    ) : null}
                </>
            );
        } else {
            const sellerFee = feeBasis.seller.fee ? `${formatDollars(feeBasis.seller.fee, { dec: 3 })}${FeeUnitsDisplayShort[feeBasis.seller.feeUnit as keyof typeof FeeUnits]}` : '$0';
            const sellerStorageFee = feeBasis.seller.storageFee ? `${formatDollars(feeBasis.seller.storageFee, { dec: 3 })}${StorageFeeUnitsDisplayShort[feeBasis.seller.storageFeeUnit as keyof typeof StorageFeeUnits]}` : null;
            return (
                <>
                    {(permissions.isAdmin || permissions.isDeveloper) ? (
                        <Card.Content className="fees-note-content">
                            <div className="fee-basis-display">
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <span>Seller fees
                                        <Popup
                                            trigger={<Icon name="info circle" style={{ marginLeft: '4px', cursor: 'pointer', color: 'var(--color-secondary)' }} />}
                                            content={sellerFee === '$0' ? (hasPvWattage ? 'Buyer pays fees on this deal.' : NO_FEE_BASIS_MESSAGE) : 'Success fee charged to the seller'}
                                            position="top center"
                                            inverted
                                        />
                                    </span><div>{sellerFee}</div>
                                </div>
                                {sellerStorageFee && (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <span>Seller storage fees
                                            <Popup
                                                trigger={<Icon name="info circle" style={{ marginLeft: '4px', cursor: 'pointer', color: 'var(--color-secondary)' }} />}
                                                content={'Success fee charged to the seller'}
                                                position="top center"
                                                inverted
                                            />
                                        </span><div>{sellerStorageFee}</div>
                                    </div>
                                )}
                            </div>
                        </Card.Content>
                    ) : null}
                </>
            );
        }
    };

    return (
        <div className="bid-cards-carousel">
            <div
                ref={carouselRefHeader}
                className="bid-cards-container bid-cards-container--sticky"
                onScroll={checkScrollPosition}
            >
                {defaultSortedBidData?.map((investorBidData: any, index: number) => {
                    const cardId = `${investorBidData.investor.id}-${index}-header`;
                    const declined = isInvestorDeclined(investorBidData);
                    return (
                        <Card key={cardId} className={`project-bid-card portfolio-bid-card${declined ? ' is-declined' : ''}`}>
                            {/* Investor Header */}
                            <Card.Content className="investor-header">
                                <div className="investor-info">
                                    <div className="investor-logo-container">
                                        {investorBidData?.investor?.company?.logoS3Url ? (
                                            <img
                                                src={investorBidData.investor.company.logoS3Url}
                                                alt={`${investorBidData.investor.company.name} logo`}
                                                className="investor-logo"
                                            />
                                        ) : (
                                            <div className="placeholder-logo"></div>
                                        )}
                                    </div>
                                    <div className="investor-details">
                                        <div className="investor-name" title={investorBidData.investor?.company?.name || 'Unknown Investor'}>
                                            {getInvestorName(investorBidData)}
                                            {permissions?.isAdmin && investorBidData.investor?.company?.id ? (
                                                <Link
                                                    to={`/company-profile/${investorBidData.investor.company.id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                    title="Open company profile in new tab"
                                                    style={{ marginLeft: 8, fontSize: 12, textDecoration: 'none' }}
                                                >
                                                    <Icon name="external" />
                                                </Link>
                                            ) : null}
                                        </div>
                                        <div className="last-updated">Updated: {getLastUpdated(investorBidData)}</div>
                                    </div>
                                </div>
                            </Card.Content>
                        </Card>
                    );
                })}
            </div>


            {/* Carousel Navigation */}
            {defaultSortedBidData.length > 1 && (
                <div className="carousel-nav-container">
                    <Button
                        className="carousel-nav carousel-nav-left"
                        icon="chevron left"
                        onClick={() => scrollCarousel('left')}
                        disabled={!canScrollLeft}
                    />
                    <Button
                        className="carousel-nav carousel-nav-right"
                        icon="chevron right"
                        onClick={() => scrollCarousel('right')}
                        disabled={!canScrollRight}
                    />
                </div>
            )}

            {/* Carousel Container */}
            <div
                ref={carouselRef}
                className="bid-cards-container"
                onScroll={checkScrollPosition}
            >
                {defaultSortedBidData?.map((investorBidData: any, index: number) => {
                    const cardId = `${investorBidData.investor.id}-${index}`;
                    const projectsArray = investorBidData.projects ? Object.values(investorBidData.projects) : [];
                    const bidCount = projectsArray.reduce((count: number, project: any) => {
                        const bid = project.projectBids?.find((bid: any) => bid.bidStatus === 'PENDING');
                        return bid?.amount ? count + 1 : count;
                    }, 0);
                    const awaitingBid = bidCount < projectsArray.length;
                    const investorRfis = getRfiDataForInvestor(investorBidData.investor?.company?.id);
                    const hasRfis = investorRfis && investorRfis.length > 0;
                    const declined = isInvestorDeclined(investorBidData);

                    return (
                        <>
                            <Card key={cardId} className={`project-bid-card portfolio-bid-card bid-card-body${declined ? ' is-declined' : ''}`}>
                                {/* Bid Value Section */}
                                <Card.Content className="bid-content">
                                    {
                                        <div className="bid-value-section">
                                            {portfolio.projects[0]?.pricingType === PricingType.OFFTAKE ? (
                                                <div className="bid-amount">
                                                    {calculateBidValue(investorBidData, 'KWH')}
                                                </div>
                                            ) : budgetDisplayMode === 'unit' ? (
                                                <div className="bid-amount primary">
                                                    {calculateBidValue(investorBidData, 'WDC')}
                                                </div>
                                            ) : (
                                                <div className="bid-amount primary">
                                                    {calculateBidValue(investorBidData, 'TOTAL')}
                                                </div>
                                            )}
                                        </div>
                                    }
                                    <div className="portfolio-actions">
                                        <ButtonGroup primary size="small" className='button-group'>
                                            <ActionsButton
                                                investor={investorBidData.investor}
                                                awaitingBid={awaitingBid}
                                                setAcceptInvestorId={setAcceptInvestorId}
                                            />
                                            <Dropdown
                                                className='button icon small'
                                                style={{ '--color-primary': 'var(--color-secondary)' }}
                                                trigger={<></>}
                                                portalId='root'
                                            >
                                                <Dropdown.Menu>
                                                    <ActionsDropdownMenu
                                                        investor={investorBidData.investor}
                                                        awaitingBid={awaitingBid}
                                                        setDeclineInvestorId={setDeclineInvestorId}
                                                    />
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </ButtonGroup>
                                    </div>
                                </Card.Content>


                                {/* Project Budget Tables */}
                                <Card.Content className="projects-content">
                                    <Header as="h5" className="card-subheading">Project Bids</Header>
                                    {renderProjectBudgetTables(investorBidData)}
                                </Card.Content>

                                <Header as="h5" className="card-subheading">Conductor Fees</Header>
                                {/* Conductor Fees Note */}
                                {buildFeeBasisDiplay('seller')}
                                {buildFeeBasisDiplay('buyer')}

                                {/* Supporting Information - Shown if RFI responses exist */}
                                {hasRfis && (
                                    <Card.Content className="rfi-content">
                                        <Header as="h4" className="card-subheading">Supporting Information</Header>
                                        <RfiResponseList
                                            rfiResponses={investorRfis}
                                            investorName={getInvestorName(investorBidData)}
                                            type="portfolio"
                                            tableView={false}
                                        />
                                    </Card.Content>
                                )}

                                {/* Activity checklist */}
                                {(permissions?.isAdmin || permissions?.isDeveloper) && (
                                    <Card.Content className="rfi-content">
                                        <Header as="h4" className="card-subheading">Activity</Header>
                                        <InvestorActivityChecklistSingle
                                            activity={activityByCompanyId[investorBidData.investor?.company?.id]}
                                            showDates={permissions?.isAdmin}
                                            hasSummaryDoc={hasSummaryDoc}
                                        />
                                    </Card.Content>
                                )}
                            </Card>
                        </>
                    );
                })}
            </div>

            {defaultSortedBidData?.length === 0 && (
                <div className="empty-state">
                    <Header as="h4">You will receive an email notification once investors have been invited to bid.</Header>
                    <p>
                        Here&rsquo;s what&rsquo;s happening:
                        <ol>
                            <li>Your project is being reviewed for completeness, to ensure it will attract optimal bids.</li>
                            <li>
                                Conductor uses the project information that you&rsquo;ve entered to identify the best investors. We evaluate your project&rsquo;s alignment with over 100 investors across market fit, deal fit, project fit, partner fit, and recent performance. This surfaces the investors most likely to bid aggressively and follow-through on their deal terms.
                            </li>
                            <li>
                                When Conductor matches your project with an investor and invites them to bid, you&rsquo;ll receive an email notification.
                            </li>
                        </ol>
                    </p>
                </div>
            )}

            {/* Modals */}
            {acceptInvestorId && (
                <AcceptPortfolioBidModal
                    investorId={acceptInvestorId}
                    closeModal={() => setAcceptInvestorId('')}
                />
            )}
            {declineInvestorId && (
                <DeclinePortfolioBidModal
                    investorId={declineInvestorId}
                    closeModal={() => setDeclineInvestorId('')}
                />
            )}
        </div>
    );
};

export default PortfolioBidCards;